import React, { useRef, useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

const ContactPremium = () => {
  const sectionRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  useGSAP(
    () => {
      gsap.from(".contact-field", {
        x: -30,
        opacity: 0,
        stagger: 0.12,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
        },
      });
    },
    { scope: sectionRef }
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const API_URL = process.env.REACT_APP_API_URL || "/api/send-email";
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setStatus("success");
        setForm({ name: "", email: "", company: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="relative overflow-hidden py-24 sm:py-28 bg-slate-50">
      <div className="absolute inset-0 bg-slate-50" aria-hidden="true" />
      <div className="premium-glow decor-drift pointer-events-none absolute inset-0 opacity-60" aria-hidden="true" />
      <div
        className="decor-pulse pointer-events-none absolute inset-0 opacity-18"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.12) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden="true">
        <div className="decor-float absolute right-8 top-8 text-[96px] font-black leading-none text-transparent [background:linear-gradient(135deg,rgba(226,232,240,0.26),rgba(148,163,184,0.08))] bg-clip-text">
          24m
        </div>
        <div className="decor-float absolute left-8 bottom-6 text-[86px] font-black leading-none text-transparent [background:linear-gradient(135deg,rgba(203,213,225,0.24),rgba(148,163,184,0.08))] bg-clip-text [animation-delay:1s]">
          18m
        </div>
      </div>
      <div className="section-shell relative z-10 grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <p className="section-kicker !text-slate-900">Contact</p>
          <h2 className="mt-3 text-3xl font-bold !text-slate-900 sm:text-4xl">Let's Build with Purpose</h2>
          <p className="mt-4 text-sm !text-slate-900">
            Tell us about your project and we’ll design the right engineering path from strategy to delivery.
          </p>

          <div className="mt-8 space-y-4">
            <div className="premium-card flex items-center gap-3 p-4 text-sm !text-slate-900 bg-slate-900/20 backdrop-blur-sm">
              <MapPin size={18} className="text-amberSoft" />
              Kgali, Kicukiro, KK 357 st.
            </div>
            <div className="premium-card flex items-center gap-3 p-4 text-sm !text-slate-900 bg-slate-900/20 backdrop-blur-sm">
              <Phone size={18} className="text-amberSoft" />
              +250 788 123 456
            </div>
            <div className="premium-card flex items-center gap-3 p-4 text-sm !text-slate-900 bg-slate-900/20 backdrop-blur-sm">
              <Mail size={18} className="text-amberSoft" />
              hello@b-fos.rw
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="contact-field premium-card space-y-4 p-6 lg:col-span-3 bg-slate-900/20 backdrop-blur-sm">
          {[
            { name: "name", label: "Full Name", type: "text" },
            { name: "email", label: "Email Address", type: "email" },
            { name: "company", label: "Company", type: "text" },
          ].map((field) => (
            <div
              key={field.name}
              className={`form-group contact-field relative ${form[field.name] ? "has-value" : ""}`}
            >
              <input
                type={field.type}
                name={field.name}
                value={form[field.name]}
                onChange={onChange}
                placeholder={field.label}
                className="form-input"
              />
              <label className="floating-label">{field.label}</label>
            </div>
          ))}

          <div className={`form-group contact-field relative ${form.message ? "has-value" : ""}`}>
            <textarea
              name="message"
              rows="6"
              value={form.message}
              onChange={onChange}
              placeholder="Project Details"
              className="form-textarea"
            />
            <label className="floating-label">Project Details</label>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={status === "sending"}
              className="contact-field inline-flex rounded-xl bg-amberSoft px-6 py-3 text-sm font-semibold text-slate-950 transition-transform duration-300 hover:-translate-y-1 hover:bg-amber-300 disabled:opacity-60"
            >
              {status === "sending" ? "Sending..." : "Send Inquiry"}
            </button>
            {status === "success" && <p className="text-sm text-green-600">Message sent — thank you.</p>}
            {status === "error" && <p className="text-sm text-red-600">Failed to send. Try again later.</p>}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactPremium;
